import React from 'react';
import { Check, Lock, ChevronRight, Clock, Bookmark } from 'lucide-react';

interface Module {
  id: string;
  title: string;
  description: string;
  duration: string;
  lessons: number;
  isLocked: boolean;
  isFree: boolean;
}

interface AcademyModulesProps {
  isAuthenticated: boolean;
  completedModules: string[];
  onModuleClick: (moduleId: string) => void;
  bookmarkedModules?: string[];
  onToggleBookmark?: (moduleId: string, title: string) => void;
}

const MODULES: Module[] = [
  {
    id: 'coffee-101',
    title: 'Coffee Business 101',
    description: 'Industry fundamentals and what it takes to succeed.',
    duration: '45 min',
    lessons: 6,
    isLocked: false,
    isFree: true
  },
  {
    id: 'finding-niche',
    title: 'Finding Your Niche',
    description: 'Target market and customer personas.',
    duration: '35 min',
    lessons: 5,
    isLocked: false,
    isFree: true
  },
  {
    id: 'branding-basics',
    title: 'Branding Basics',
    description: 'Logo, packaging, and storytelling.',
    duration: '50 min',
    lessons: 7,
    isLocked: false,
    isFree: true
  },
  {
    id: 'marketing-strategy',
    title: 'Marketing Strategy',
    description: 'Social media and customer acquisition.',
    duration: '60 min',
    lessons: 8,
    isLocked: true,
    isFree: false
  },
  {
    id: 'scaling-up',
    title: 'Scaling Up',
    description: 'Operations and growth strategies.',
    duration: '55 min',
    lessons: 7,
    isLocked: true,
    isFree: false
  },
  {
    id: 'customer-retention',
    title: 'Customer Retention',
    description: 'Loyalty and lifetime value.',
    duration: '40 min',
    lessons: 5,
    isLocked: true,
    isFree: false
  }
];

const AcademyModules: React.FC<AcademyModulesProps> = ({
  isAuthenticated,
  completedModules,
  onModuleClick,
  bookmarkedModules = [],
  onToggleBookmark
}) => {
  const getModuleStatus = (module: Module): 'completed' | 'locked' | 'available' => {
    if (completedModules.includes(module.id)) return 'completed';
    if (module.isLocked && !isAuthenticated) return 'locked';
    if (!module.isFree && !isAuthenticated) return 'locked';
    return 'available';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-neutral-900">Modules</h2>
        <span className="text-sm text-neutral-400">
          {completedModules.length} of {MODULES.length}
        </span>
      </div>

      <div className="space-y-2">
        {MODULES.map((module, index) => {
          const status = getModuleStatus(module);
          const isBookmarked = bookmarkedModules.includes(module.id);

          return (
            <div
              key={module.id}
              className={`w-full bg-white rounded-2xl p-4 border transition-all flex items-center ${
                status === 'locked'
                  ? 'border-neutral-100 opacity-50'
                  : 'border-neutral-200/60 hover:border-neutral-300'
              }`}
            >
              {/* Number / Status */}
              <button
                onClick={() => onModuleClick(module.id)}
                disabled={status === 'locked'}
                className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold ${
                  status === 'completed'
                    ? 'bg-emerald-50 text-emerald-600'
                    : status === 'locked'
                    ? 'bg-neutral-50 text-neutral-300'
                    : 'bg-neutral-900 text-white'
                } ${status !== 'locked' ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              >
                {status === 'completed' ? (
                  <Check className="w-5 h-5" />
                ) : status === 'locked' ? (
                  <Lock className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </button>

              {/* Content */}
              <button
                onClick={() => onModuleClick(module.id)}
                disabled={status === 'locked'}
                className={`flex-1 min-w-0 text-left ${status !== 'locked' ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              >
                <h3 className={`font-semibold text-sm ${status === 'locked' ? 'text-neutral-400' : 'text-neutral-900'}`}>
                  {module.title}
                </h3>
                <div className="flex items-center mt-1 text-xs text-neutral-400">
                  <Clock className="w-3 h-3 mr-1" />
                  <span>{module.duration}</span>
                  <span className="mx-1.5">·</span>
                  <span>{module.lessons} lessons</span>
                </div>
              </button>

              {/* Bookmark Button */}
              {status !== 'locked' && onToggleBookmark && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleBookmark(module.id, module.title);
                  }}
                  className="p-2 rounded-lg hover:bg-neutral-100 transition-colors mr-1"
                >
                  <Bookmark
                    className={`w-4 h-4 ${isBookmarked ? 'fill-neutral-900 text-neutral-900' : 'text-neutral-300'}`}
                  />
                </button>
              )}

              {/* Arrow */}
              {status !== 'locked' && (
                <ChevronRight className="w-5 h-5 text-neutral-300 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AcademyModules;
